import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Gift, Copy, EnvelopeSimple, Users, Trophy,
  ShareNetwork, Check, ArrowRight,
} from "@phosphor-icons/react";
import { devWarn } from "../lib/logger";

export default function Referral() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/referral/my-code");
      setData(r.data);
    } catch (e) {
      toast.error("Errore caricamento codice referral");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const link = data ? `${window.location.origin}/register?ref=${data.code}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "ETA Build - Invito amico",
          text: `Ti invito su ETA Build: ${data.reward_percent}% di sconto per ${data.reward_months} mesi.`,
          url: link,
        });
      } catch (e) { devWarn("native share canceled or unsupported", e); }
    } else {
      copyLink();
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!email) return;
    setInviting(true);
    try {
      await api.post("/referral/invite", {
        email, message, origin_url: window.location.origin,
      });
      toast.success(`Invito inviato a ${email}`);
      setEmail(""); setMessage("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio");
    } finally { setInviting(false); }
  };

  if (loading) {
    return <Layout><div className="text-[#A3A3A3] text-center py-12">Caricamento…</div></Layout>;
  }

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Invita amici</div>
        <h1 className="font-heading text-4xl font-black uppercase tracking-tighter leading-none mt-1">
          Referral
        </h1>
      </div>

      {/* Hero card */}
      <div className="bg-gradient-to-br from-[#FFD600]/20 to-[#FFD600]/0 border-2 border-[#FFD600] rounded-md p-5 mb-4" data-testid="referral-hero">
        <div className="flex items-center gap-2 text-[#FFD600] mb-2">
          <Gift size={20} weight="fill"/>
          <span className="text-xs uppercase tracking-wider font-bold">Vincete entrambi</span>
        </div>
        <div className="font-heading text-3xl font-black tracking-tighter leading-tight">
          {data?.reward_percent}% di sconto
        </div>
        <div className="text-sm text-white mt-1">
          per <b>{data?.reward_months} mesi</b> · sia a te che al tuo amico, alla sua attivazione
        </div>
      </div>

      {/* Your code */}
      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-2">Il tuo codice</div>
        <div className="bg-[#0A0A0A] rounded p-4 text-center mb-3" data-testid="my-code">
          <div className="font-mono text-2xl font-black text-[#FFD600] tracking-widest">{data?.code}</div>
        </div>
        <div className="text-xs text-[#A3A3A3] mb-2 break-all bg-[#0A0A0A] rounded px-2 py-1.5 font-mono" data-testid="ref-link">{link}</div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="copy-link-btn" onClick={copyLink}
            className="h-11 rounded-md font-black uppercase text-xs bg-[#1A1A1A] border border-[#333] hover:border-[#FFD600] flex items-center justify-center gap-1">
            {copied ? <Check size={16} weight="bold"/> : <Copy size={16} weight="bold"/>}
            {copied ? "Copiato!" : "Copia link"}
          </button>
          <button data-testid="share-btn" onClick={share}
            className="h-11 rounded-md font-black uppercase text-xs bg-[#FFD600] text-[#0A0A0A] flex items-center justify-center gap-1">
            <ShareNetwork size={16} weight="bold"/> Condividi
          </button>
        </div>
      </div>

      {/* Invite by email */}
      <form onSubmit={sendInvite} className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4" data-testid="invite-form">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-3 flex items-center gap-2">
          <EnvelopeSimple size={14} weight="bold"/> Invia invito via email
        </div>
        <input
          data-testid="invite-email" type="email" required placeholder="email@amico.it"
          value={email} onChange={(e)=>setEmail(e.target.value)}
          className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 mb-2 focus:border-[#FFD600] focus:outline-none"
        />
        <textarea
          data-testid="invite-msg" placeholder="Messaggio personale (opzionale)" rows={2}
          value={message} onChange={(e)=>setMessage(e.target.value)}
          className="w-full bg-[#0A0A0A] border-2 border-[#333] rounded-md px-3 py-2 mb-2 focus:border-[#FFD600] focus:outline-none text-sm"
        />
        <button data-testid="invite-submit" disabled={inviting}
          className="w-full bg-[#FFD600] text-[#0A0A0A] h-12 rounded-md font-black uppercase text-sm hover:bg-[#E6C200] transition disabled:opacity-50 flex items-center justify-center gap-2">
          {inviting ? "Invio…" : (<>Invita amico <ArrowRight size={16} weight="bold"/></>)}
        </button>
      </form>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4" data-testid="ref-stats">
        <StatCard label="Invitati" value={data?.stats?.invited || 0} Icon={Users}/>
        <StatCard label="Registrati" value={data?.stats?.registered || 0} Icon={Check}/>
        <StatCard label="Conferme" value={data?.stats?.qualified || 0} Icon={Trophy} highlight/>
      </div>

      {/* My rewards */}
      {(data?.rewards || []).length > 0 && (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4" data-testid="my-rewards">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-3">
            I tuoi codici sconto guadagnati
          </div>
          <div className="space-y-2">
            {data.rewards.map((r) => (
              <div key={r.id} className="bg-[#0A0A0A] rounded p-3 flex justify-between items-center">
                <div>
                  <div className="font-mono text-[#FFD600] font-bold tracking-wider">{r.code}</div>
                  <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mt-0.5">
                    {r.percent_off}% · {r.months} mesi · {r.kind === "referrer" ? "Tu invitavi" : "Tu invitato"}
                  </div>
                </div>
                <button
                  data-testid={`copy-reward-${r.code}`}
                  onClick={async () => { await navigator.clipboard.writeText(r.code); toast.success("Codice copiato"); }}
                  className="text-[#FFD600] p-2 hover:bg-[#1A1A1A] rounded">
                  <Copy size={18} weight="bold"/>
                </button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#666] text-center mt-3">
            Inserisci il codice al prossimo rinnovo (Stripe Checkout o Portale Clienti).
          </p>
        </div>
      )}

      {/* Invited list */}
      {(data?.referrals || []).length > 0 && (
        <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4" data-testid="invited-list">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-3">
            Cronologia inviti
          </div>
          <div className="space-y-1.5 text-xs">
            {data.referrals.slice(0, 20).map((r) => (
              <div key={r.id} className="flex justify-between items-center bg-[#0A0A0A] rounded px-3 py-2">
                <span className="text-white truncate">{r.invited_email || "—"}</span>
                <StatusPill status={r.status}/>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}

function StatCard({ label, value, Icon, highlight }) {
  return (
    <div className={`rounded-md p-3 border text-center ${highlight ? "border-[#FFD600] bg-[#FFD600]/10" : "border-[#333] bg-[#1A1A1A]"}`}>
      <Icon size={14} weight="bold" className="text-[#FFD600] mx-auto mb-1"/>
      <div className="font-heading text-2xl font-black">{value}</div>
      <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider font-bold">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    invited: "bg-[#1A1A1A] text-[#A3A3A3]",
    registered: "bg-[#FFD600]/20 text-[#FFD600]",
    qualified: "bg-[#00C853]/20 text-[#00C853]",
  };
  const labels = {
    invited: "Invitato",
    registered: "Registrato",
    qualified: "Confermato",
  };
  return (
    <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded ${colors[status] || colors.invited}`}>
      {labels[status] || status}
    </span>
  );
}
